import { Component, computed, effect, input, linkedSignal, model, signal } from '@angular/core';
import { AnimationOption, OrientationOption, SlideType, SortingWidgetBucket, SortingWidgetOption } from '../../../common/models';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { takeWhile } from 'rxjs';


type SortingWidgetAnswer = {
  type: SlideType.SlideSorting;

  answers: {
    questionHash: string;
    bucket: string;
  }[];
}

type SortingWidgetOptionExtended = SortingWidgetOption & {
  index: number;
}

type SortingWidgetBucketExtended = SortingWidgetBucket & {
  droppedItems?: SortingWidgetOptionExtended[];
}


@Component({
  selector: 'lib-question-sorting-widget',
  templateUrl: './question-sorting-widget.component.html',
  styleUrl: './question-sorting-widget.component.scss',
  standalone: false
})
export class QuestionSortingWidgetComponent {
  public answer = model<SortingWidgetAnswer | null>();
  public animation = input<AnimationOption>();
  public animationDelay = input<number>(0);
  public readonlyMode = input<boolean>(false);
  public buckets = input<SortingWidgetBucketExtended[]>();
  public items = input<SortingWidgetOption[]>();
  public orienation = input<OrientationOption>(OrientationOption.HORIZONTAL);

  public selectedItemIndex = signal(-1);

  public options = linkedSignal<SortingWidgetOptionExtended[]>(() =>
    this.items()?.map((item, i) => ({
      ...item,
      index: i
    })) ?? []
  );

  public bucketsState = linkedSignal(() =>
    this.buckets()?.map(bucket => ({
      ...bucket,
      droppedItems: bucket.droppedItems ?? [] as SortingWidgetOptionExtended[],
    })) ?? []
  );

  public itemsLeft = computed(() => {
    const assignedIds = new Set(
      this.bucketsState().flatMap(b => b.droppedItems.map(i => i.id))
    );

    return this.options()?.filter(item => !assignedIds.has(item.id));
  });

  private bucketsChanged = effect(() => {
    const state = this.bucketsState();
    const itemsLeft = this.itemsLeft();

    if (itemsLeft?.length > 0) {
      this.answer.set(null);
      return;
    }

    const answers  = state.reduce((total: { bucket: string; questionHash: string; }[], current) => {
      const options = current.droppedItems.map(option => ({
        bucket: current.id,
        questionHash: option.questionHash ?? option.id
      }))

      return [...total, ...options];
    }, []);

    this.answer.set({
      type: SlideType.SlideSorting,
      answers
    });
  });

  public canDrop = (_drag: CdkDrag, drop: CdkDropList) => {
    return drop.data.index !== -1;
  }

  public setSelectedItemIndex(index: number): void {
    if (this.readonlyMode()) {
      return;
    }

    this.selectedItemIndex.set(index);
  }

  public dropSelectedItemToBucket(bucketIndex: number): void {
     if (this.readonlyMode()) {
      return;
    }
    
    const itemIndex = this.selectedItemIndex();
    if (bucketIndex === -1 || itemIndex === -1) {
      return;
    }

    const item = this.itemsLeft()[itemIndex];
    this.moveItemBetweenBuckets(item, bucketIndex);
    this.selectedItemIndex.set(-1);
  }

  public drop(event: CdkDragDrop<any>): void {
    const targetBucketIndex = event.container.data.index;
    const sourceBucketIndex = event.previousContainer.data.index;
    const item = event.item.data as SortingWidgetOptionExtended;

    this.moveItemBetweenBuckets(item, targetBucketIndex, sourceBucketIndex);
    this.selectedItemIndex.set(-1);
  }

  private moveItemBetweenBuckets(item: SortingWidgetOptionExtended, targetBucketIndex: number, sourceBucketIndex = -1): void {
    this.bucketsState.update(buckets => {
      const next = structuredClone(buckets);

      if (sourceBucketIndex >= 0) {
        next[sourceBucketIndex].droppedItems = next[sourceBucketIndex].droppedItems.filter(i => i.id !== item.id);
      }

      if (targetBucketIndex >= 0) {
        next[targetBucketIndex].droppedItems.unshift(item);
      }

      return next;
    });
  }
}
